import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Group, LoadingOverlay, Select, Space, Text, TextInput } from '@mantine/core';
import { getBillingInvoiceGetBillingInvoicesForCompany, postBillingInvoiceSaveCopiedBillingInvoiceAdjustments } from '@apis/Invoices';
import { useInputState } from '@mantine/hooks';
import { BillingInvoice, BillingInvoiceAdjustment } from '@apis/Invoices/model';
import { useDi } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { NotificationService } from '@root/Services/Notification/NotificationService';

export function CopyAdjustmentModal(props: {
    companyId: number;
    adjustment: BillingInvoiceAdjustment;
    onClose: (adjustmentCopied: boolean) => void;
}) {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<JSX.Element>(<Text></Text>);
    const [error, setError] = useInputState(false);
    const [submitted, setSubmitted] = useState(false);
    const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
    const fmtSvc = useDi(FormatService);
    const companyCtx = useDi(CompanyContextService);
    const notificationSvc = useDi(NotificationService);

    const availableInvoices = useMemo(() => {
        return [...invoices]
            .sort((a, b) => {
                const dateA = new Date(a.Year!, a.Month! - 1);
                const dateB = new Date(b.Year!, b.Month! - 1);
                return dateB.getTime() - dateA.getTime();
            })
            .map((i) => ({
                label: `${fmtSvc.formatShortMonthYear(new Date(i.Year!, i.Month! - 1))}`,
                value: i.Id!.toString(),
            }));
    }, [invoices]);

    const cancel = useCallback(() => props.onClose(false), [props.onClose]);

    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const response = await getBillingInvoiceGetBillingInvoicesForCompany({ companyId: props.companyId }, companyCtx.getRequestOptions(true));
            setInvoices(response);
        } catch (e) {
            setError(true);
            setErrorMessage(<Text>Failed to load invoices.</Text>);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const invoiceId = selectedInvoice ? parseInt(selectedInvoice, 10) : 0;
        const invoiceSelected = invoices.find((x) => x.Id === invoiceId);
        if (!invoiceSelected) {
            setError(true);
            setErrorMessage(<Text>Please select an invoice to copy to.</Text>);
            return;
        }
        const adjustment = props.adjustment;
        setSubmitted(true);
        setError(false);
        setErrorMessage(<Text></Text>);

        try {
            let result = null;
            await new Promise<void>((resolve) => {
                companyCtx.withParentCompany(() => {
                    postBillingInvoiceSaveCopiedBillingInvoiceAdjustments(adjustment, { billingInvoiceId: invoiceId })
                        .then((res) => {
                            result = res;
                        })
                        .catch((err) => {
                            setError(true);
                            setErrorMessage(<Text>There was an issue copying the adjustment.</Text>);
                        })
                        .finally(resolve);
                });
            });

            if (result) {
                notificationSvc.notify(
                    'Adjustment Saved',
                    `Successfully copied Adjustment to ${fmtSvc.formatShortMonthYear(
                        new Date(invoiceSelected.Year!, invoiceSelected.Month! - 1)
                    )} invoice`,
                    'success',
                    null
                );
                props.onClose(true);
            } else {
                setError(true);
                setErrorMessage(<Text>There was an issue copying the adjustment.</Text>);
                setSubmitted(false);
            }
        } catch (e) {
            console.error('Failed to copy adjustment:', e);
            setError(true);
            setErrorMessage(<Text>Unexpected error copying the adjustment.</Text>);
            setSubmitted(false);
        }
    };

    return (
        <>
            <LoadingOverlay visible={loading} overlayBlur={2} />
            <Box p="m">
                <Select
                    data={availableInvoices}
                    label="Select Invoice to Copy To"
                    value={selectedInvoice}
                    onChange={setSelectedInvoice}
                    placeholder="Select Invoice"
                    searchable
                    nothingFound="No invoices found"
                />
            </Box>
            <div hidden={!error}>
                <Box>
                    <Space p="xs" />
                    <Alert color="error">{errorMessage}</Alert>
                </Box>
            </div>
            <Space h="md" />
            <Group position="right">
                <Button data-atid="SettingsCancelButton" onClick={cancel} variant="outline">
                    Cancel
                </Button>
                <Button data-atid="CopyAdjustmentButton" onClick={handleSave} variant={!submitted ? 'filled' : 'light'}>
                    {!submitted ? 'Copy Adjustment' : 'Copying adjustment...'}
                </Button>
            </Group>
        </>
    );
}
